import { GenericModal } from '@/components/shared/GenericModal'
import { DeleteConfirmationModal } from '@/components/shared/DeleteConfirmationModal'
import { useEffect, useMemo, useState } from 'react'
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { dialogOverlay } from '../NewComponent/styles'
import { Box, DialogContent, DialogActions, IconButton, InputAdornment, List, ListItem, Radio, TextField, Typography, Button, Divider, CircularProgress } from '@mui/material'
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { abyssColors } from '@/theme/colors';
import * as state from '@/components/Analysis/state';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useUpdateInspectionPlanMutation } from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useFeatureFlag, useHavePermission } from '@/hooks';
import { Permissions } from '@/types';
import { applyInspectionPlanFilters } from '@/utils';
import { useInspectionPlanDelete } from '@/components/Analysis/Insights/hooks/useInspectionPlanDelete';


type LoadViewProps = {
  open: boolean;
  handleCloseView?: (view: boolean) => void;
};

export const LoadViewModal = ({ open, handleCloseView }: LoadViewProps) => {
  const [plans, setPlans] = useAtom(state.inspectionPlansState);
  const [pinInspectionPlan] = useUpdateInspectionPlanMutation();
  const structureId = useGetStructureId();
  const { showSnackBar } = useSnackBarMessage({});
  const isAdmin = useFeatureFlag('admin');
  const canManageInspectionPlans = useHavePermission(Permissions.MANAGE_INSPECTION_PLANS)

  const setSelectedTemplateFilters = useSetAtom(state.selectedTemplateFiltersState);
  const setSelectedDeckIds = useSetAtom(state.selectedFilteredDeckIds);
  const setSelectedWorkpackIds = useSetAtom(state.selectedFilteredWorkpackIds);
  const setSelectedPaintRegionIds = useSetAtom(state.selectedFilteredPaintRegionIds);
  const setApplyFilters = useSetAtom(state.applyFilters);
  const setSelectedPlanId = useSetAtom(state.selectedInspectionPlanId);
  const currentlyLoadedPlanId = useAtomValue(state.selectedInspectionPlanId);

  const [selectedId, setSelectedId] = useState<string>('');
  const [search, setSearch] = useState("");
  const { deleteState, handleDelete, confirmDelete, cancelDelete } = useInspectionPlanDelete();
  const [pinningPlanId, setPinningPlanId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (currentlyLoadedPlanId) {
      setSelectedId(currentlyLoadedPlanId);
    }
  }, [open, currentlyLoadedPlanId]);

  const handlePinToggle = async (planId: string, currentPinned: boolean) => {
    try {
      // allow if user is admin OR has the manage inspection plans permission
      if (!isAdmin && !canManageInspectionPlans) {
        showSnackBar('user does not have permission to update this view', 'error');
        return;
      }
      setPinningPlanId(planId);
      await pinInspectionPlan({
        variables: {
          input: {
            planId,
            structureId,
            isPlanPinned: !currentPinned,
          },
        },
      });
      setPlans((prev) => prev.map((plan) => (plan.id === planId ? { ...plan, isPlanPinned: !currentPinned } : plan)));
      showSnackBar(!currentPinned ? 'Plan pinned!' : 'Plan unpinned!', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      showSnackBar(message, 'error');
    } finally {
      setPinningPlanId(null);
    }
  };

  const filteredInspectionPlan = useMemo(
    () => plans.filter((plan) => plan.name.toLowerCase().includes(search.toLowerCase())),
    [search, plans],
  );

  const sortedPlans = useMemo(() => {
    return [...filteredInspectionPlan].sort((a, b) => {
      if (a.isPlanPinned === b.isPlanPinned) {
        return a.name.localeCompare(b.name);
      }
      return Number(b.isPlanPinned) - Number(a.isPlanPinned);
    });
  }, [filteredInspectionPlan]);

  const applyPlanFilters = (planId: string) => {
    const selectedPlan = plans.find((p) => p.id === planId);
    if (!selectedPlan) {
      showSnackBar('Selected plan not found', 'error');
      return;
    }
    const {
      templateFilters,
      deckIds,
      workpackIds,
      paintRegionIds,
      planId: returnedPlanId,
    } = applyInspectionPlanFilters(selectedPlan);

    setSelectedTemplateFilters(templateFilters);
    setSelectedDeckIds(deckIds);
    setSelectedWorkpackIds(workpackIds);
    setSelectedPaintRegionIds(paintRegionIds);
    setSelectedPlanId(returnedPlanId);
    setApplyFilters(true);
  };


  return (
    <GenericModal
      open={open}
      title='Load saved views'
      description='Select one of your saved views to load it into Abyss Fabric.'
      handleClose={() => handleCloseView?.(false)}
      titleSx={{ fontWeight: 600, mt: '2.4rem' }}
      descriptionSx={{ mt: '1.2rem' }}
      backgroundPaperStyle={{
        minHeight: '30rem',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        backgroundColor: '#FFF',
      }}
      sx={{ ...dialogOverlay, width: '60rem', height: 'fit-content' }}
    >
      <DialogContent
        sx={{
          px: 2,
          pt: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
        }}
      >
        <TextField
          size="small"
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: '92%', ml: 2, mb: '1.6rem' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <List
          sx={{
            overflowY: 'auto',
            flex: 1,
            maxHeight: '24rem',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          {sortedPlans.map((view) => (
            <ListItem
              sx={{ pt: 0, pb: 0, cursor: 'pointer' }}
              key={view.id}
              onClick={() => setSelectedId(view.id)}
              secondaryAction={
                <Box>
                  {view.id && (
                    <>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePinToggle(view.id, !!view.isPlanPinned);
                        }}
                        color={view.isPlanPinned ? 'primary' : 'default'}
                      >
                        {pinningPlanId === view.id ? (
                          <CircularProgress size={20} />
                        ) : view.isPlanPinned ? (
                          <PushPinIcon />
                        ) : (
                          <PushPinOutlinedIcon />
                        )}
                      </IconButton>

                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(view.id);
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </>
                  )}
                </Box>
              }
            >
              <Radio
                checked={selectedId === view.id}
                onChange={() => setSelectedId(view.id)}
                onClick={(e) => e.stopPropagation()}
              />
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 400, color: abyssColors.primary[500] }}>
                {view.name}
              </Typography>

            </ListItem>
          ))}
        </List>

      </DialogContent>
      <Divider sx={{ my: 0, mt: 1 }} />
      <DialogActions sx={{ px: 3, py: 4 }}>
        <Button variant="outlined" onClick={() => handleCloseView?.(false)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            const planToApply = selectedId;
            if (!planToApply) {
              handleCloseView?.(false);
              return;
            }
            const planExists = plans.some(plan => plan.id === planToApply);
            if (!planExists) {
              handleCloseView?.(false);
              return;
            }
            applyPlanFilters(planToApply);
            handleCloseView?.(false);
          }}
        >
          Confirm
        </Button>
      </DialogActions>
      <DeleteConfirmationModal
        title={'Are you sure ?'}
        isOpen={deleteState.isOpen}
        itemName="this plan"
        description="Are you sure you want to delete this plan? This action will permanently remove the plan and its data."
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        handleClose={cancelDelete}
        sxStyle={{ bottom: '10% !important' }}
      />
    </GenericModal>
  );

}
